import { useMutation, useQuery } from "@apollo/client";
import mixpanel from "mixpanel-browser";
import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import {
  SOAP_FIELDS,
  TEMPLATE_TYPES,
  TRANSCRIPT_SAMPLE_MAPPINGS,
} from "./constants";
import TemplateBuilder from "./TemplateBuilder";
import {
  processSoapFields,
  processField,
} from "./TemplateBuilder/utils";
import {
  UPDATE_AUTO_TEMPLATE,
  DELETE_AUTO_TEMPLATE,
} from "../../graphql/mutations/AutoTemplates";
import { GET_AUTO_TEMPLATE_BY_UUID } from "../../graphql/queries/AutoTemplates";
import { useRecordingJobType } from "../../hooks";
import { alert } from "../common/Alert";

function resetFormDataFromQuery(data, reset, templateVariant, isNew) {
  if (data?.autoTemplateById) {
    const template = data?.autoTemplateById;
    const templateFields = template.fields?.map((field) =>
      processField(field.fieldName, field.fieldValue, field.fieldIdx),
    );
    const sortedTemplateFields = templateFields.sort(
      (a, b) => a.fieldIdx - b.fieldIdx,
    );
    reset({
      uuid: template.uuid,
      name: !isNew ? template.name : "",
      description: template.description ?? "",
      templateType: template.templateType ?? TEMPLATE_TYPES.SOAP,
      isFavourite: template.isFavourite ?? false,
      userPreferences: template.userPreferences ?? "",
      pdfStorageObjectName: template.pdfStorageObjectName ?? "",
      preview: template.templatePreviews[0]?.previewContent || "",
      templateFields: sortedTemplateFields,
      sampleTranscript:
        TRANSCRIPT_SAMPLE_MAPPINGS.DEFAULT.sampleTranscript,
      templateVariant: templateVariant ?? "DEFAULT",
    });
  }
}

const AutoTemplateForm = () => {
  const [templateConstructionChoice, setTemplateConstructionChoice] =
    useState("scratch");
  const navigate = useNavigate();
  const { uuid } = useParams();
  const {
    selectedJobType,
    setSelectedJobType,
    resetSelectedJobType,
  } = useRecordingJobType();
  const [searchParams] = useSearchParams();
  const isNew = searchParams.get("isNew") === "true";

  const [shouldShowDeleteModal, setShouldShowDeleteModal] =
    useState(false);

  const [updateAutoTemplate] = useMutation(UPDATE_AUTO_TEMPLATE);
  const [deleteAutoTemplate] = useMutation(DELETE_AUTO_TEMPLATE);

  const methods = useForm({
    defaultValues: {
      uuid: "",
      name: "",
      description: "",
      templateType: TEMPLATE_TYPES.SOAP,
      isFavourite: false,
      userPreferences: {},
      pdfStorageObjectName: "",
      preview: "",
      templateFields: processSoapFields(SOAP_FIELDS),
      sampleTranscript:
        TRANSCRIPT_SAMPLE_MAPPINGS.DEFAULT.sampleTranscript,
      templateVariant: "DEFAULT",
    },
  });

  const { control, reset } = methods;

  const { refetch, loading, error } = useQuery(
    GET_AUTO_TEMPLATE_BY_UUID,
    {
      variables: { uuid },
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        resetFormDataFromQuery(data, reset, "DEFAULT", isNew);
      },
    },
  );

  const onSubmit = async (formData) => {
    if (!formData.name) {
      alert("warning", "Please enter a template name to save.");
      return;
    }

    if (formData.templateType == "CUSTOM") {
      if (!formData.templateFields[0].fieldValue) {
        alert("warning", "Please enter a template to save.");
        return;
      }
    } else {
      //only one field value because some users might not have specific subheadings for all SOAP fields, just want to put specific user instructions
      if (
        !formData.templateFields[0].fieldValue &&
        !formData.templateFields[1].fieldValue &&
        !formData.templateFields[2].fieldValue &&
        !formData.templateFields[3].fieldValue
      ) {
        alert("warning", "Please enter a SOAP field value to save.");
        return;
      }
    }

    // If the template is the currently selected jobType, update the name on the dropdown
    if (selectedJobType?.value === uuid) {
      setSelectedJobType({
        ...selectedJobType,
        name: formData.name,
      });
    }
    try {
      await updateAutoTemplate({
        variables: {
          input: {
            templateUuid: uuid,
            name: formData.name,
            description: formData.description,
            userPreferences: formData.userPreferences,
            templateType: formData.templateType,
            pdfStorageObjectName: formData.pdfStorageObjectName,
            templateFields: formData.templateFields,
            isFavourite: formData.isFavourite,
          },
        },
      });

      refetch({
        variables: {
          uuid,
        },
      });
      const templateLogString = isNew ? "created" : "updated";
      mixpanel.track(`Template ${templateLogString}`, {
        templateUUID: uuid,
        templateName: formData.name,
        templateType: formData.templateType,
        isUserPreferencesFilled: !!formData.userPreferences,
        isPreviewFilled: !!formData.preview,
        templateConstructionChoice,
      });
      alert("success", `Template ${templateLogString} successfully.`);
      navigate("/templates");
    } catch (error) {
      if (
        error.message ===
        "Template with the same name already exists."
      ) {
        alert(
          "error",
          "Please rename this template. You already have a template with this name.",
        );
        mixpanel.track("Template name conflict", {
          templateName: formData.name,
        });
      } else {
        alert("error", "Error updating template. Please try again.");
        mixpanel.track("Template update error", {
          templateType: formData.templateType,
          error: error.message,
        });
      }
    }
  };

  const handleDelete = async (bypassModal = false) => {
    if (bypassModal) {
      handleConfirmDelete(bypassModal);
      return;
    }
    setShouldShowDeleteModal(true);
  };

  const handleConfirmDelete = async (bypassModal = false) => {
    try {
      await deleteAutoTemplate({
        variables: { templateUuid: uuid },
      });
      if (selectedJobType?.value === uuid) {
        resetSelectedJobType();
      }
      navigate("/templates");
      alert("success", "Template deleted successfully.");
    } catch (error) {
      console.error("Error deleting template:", error);
      alert("error", "Error deleting template. Please try again.");
    }
    if (!bypassModal) {
      setShouldShowDeleteModal(true);
    }
  };

  if (error) {
    alert("error", error.message);
    navigate("/templates");
  }

  return (
    <>
      {!loading && (
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <TemplateBuilder
              control={control}
              title={isNew ? "Create Template" : "Edit Template"}
              isNew={isNew}
              handleDelete={handleDelete}
              shouldShowDeleteModal={shouldShowDeleteModal}
              setShouldShowDeleteModal={setShouldShowDeleteModal}
              refetch={refetch}
              templateConstructionChoice={templateConstructionChoice}
              setTemplateConstructionChoice={
                setTemplateConstructionChoice
              }
            />
          </form>
        </FormProvider>
      )}
    </>
  );
};

export default AutoTemplateForm;
